/**
 * Admin User Detail Page
 *
 * Displays comprehensive information about a single user including
 * all related data from various tables.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAdminUserDetail } from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import type { AdminUserDetailResponse } from '../types/admin.js';

export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const [data, setData] = useState<AdminUserDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  const loadUser = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getAdminUserDetail(userId);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', count: null },
    { id: 'nfts', label: 'NFTs', count: data?.stats.nftCount },
    { id: 'collection-offers', label: 'Collection Offers', count: data?.stats.collectionOfferCount },
    { id: 'transactions', label: 'Transactions', count: data?.stats.transactionCount },
    { id: 'alerts', label: 'Alerts', count: data?.stats.alertCount },
    { id: 'notifications', label: 'Notifications', count: data?.stats.notificationCount },
    { id: 'follows', label: 'Follows', count: (data?.stats.followedWalletCount || 0) + (data?.stats.walletFollowCount || 0) },
    { id: 'conversations', label: 'Messages', count: data?.stats.conversationCount },
    { id: 'referrals', label: 'Referrals', count: data?.stats.referralCount },
    { id: 'sessions', label: 'Sessions', count: data?.sessions?.length },
    ...(data?.user.isAdmin ? [{ id: 'audit', label: 'Audit Log', count: data?.auditLog?.length }] : []),
  ];

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Back Link */}
        <div className="mb-4">
          <Link to="/admin/users" className="text-blue-600 hover:text-blue-800 text-sm">
            ← Back to Users
          </Link>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadUser} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && !data && (
          <div className="bg-white rounded-lg shadow p-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        )}

        {data && (
          <>
            {/* User Header */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-start gap-6">
                {data.user.avatarUrl && (
                  <img
                    src={data.user.avatarUrl}
                    alt=""
                    className="w-20 h-20 rounded-full"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold text-gray-900">
                      {data.user.username || data.user.displayName || 'Anonymous User'}
                    </h1>
                    {data.user.isAdmin && (
                      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                        Admin
                      </span>
                    )}
                    {data.user.isVerified && (
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        Verified
                      </span>
                    )}
                    {data.user.hasXamanToken && (
                      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                        Xaman Connected
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-sm text-gray-600 mb-2">{data.user.xrplAddress}</p>
                  {data.user.bio && (
                    <p className="text-gray-600 text-sm mb-2">{data.user.bio}</p>
                  )}
                  <p className="text-sm text-gray-500">
                    Joined {formatShortDate(data.user.createdAt)} • Last updated {formatShortDate(data.user.updatedAt)}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">User ID</div>
                  <div className="font-mono text-xs text-gray-600">{data.user.id}</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex gap-4 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-1 border-b-2 text-sm font-medium whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                    {tab.count !== null && tab.count !== undefined && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100">
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow">
              {activeTab === 'overview' && (
                <OverviewTab data={data} formatDate={formatDate} />
              )}
              {activeTab === 'nfts' && (
                <NftsTab nfts={data.nfts} />
              )}
              {activeTab === 'collection-offers' && (
                <CollectionOffersTab offers={data.collectionOffers} formatDate={formatDate} />
              )}
              {activeTab === 'transactions' && (
                <TransactionsTab
                  transactions={data.transactions}
                  userAddresses={[data.user.xrplAddress, ...data.wallets.map(w => w.address)]}
                  formatDate={formatDate}
                  truncateAddress={truncateAddress}
                />
              )}
              {activeTab === 'alerts' && (
                <AlertsTab alerts={data.alertConfigs} formatDate={formatDate} />
              )}
              {activeTab === 'notifications' && (
                <NotificationsTab notifications={data.notifications} formatDate={formatDate} />
              )}
              {activeTab === 'follows' && (
                <FollowsTab
                  followedWallets={data.followedWallets}
                  walletFollows={data.walletFollows}
                  formatDate={formatDate}
                  truncateAddress={truncateAddress}
                />
              )}
              {activeTab === 'conversations' && (
                <ConversationsTab
                  conversations={data.conversations}
                  collectionMessages={data.collectionMessages}
                  userAddresses={[data.user.xrplAddress, ...data.wallets.map(w => w.address)]}
                  formatDate={formatDate}
                  truncateAddress={truncateAddress}
                />
              )}
              {activeTab === 'referrals' && (
                <ReferralsTab referrals={data.referrals} referrer={data.user.referrer} formatDate={formatDate} truncateAddress={truncateAddress} />
              )}
              {activeTab === 'sessions' && (
                <SessionsTab sessions={data.sessions} formatDate={formatDate} />
              )}
              {activeTab === 'audit' && data.user.isAdmin && (
                <AuditLogTab auditLog={data.auditLog} formatDate={formatDate} />
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

// ==================== Tab Components ====================

function ListingStatsCard({ stats }: { stats: AdminUserDetailResponse['listingStats'] }) {
  return (
    <div className="border rounded-lg p-4">
      <h3 className="font-medium text-gray-900 mb-3">Listings</h3>
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-gray-500">Total NFTs Listed</dt>
          <dd className="text-lg font-semibold text-gray-900">{stats.totalListed}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">On xrp.deals</dt>
          <dd className="text-gray-900">
            {stats.anyListedOnDeals}
            {stats.totalListed > 0 && (
              <span className="ml-2 text-xs text-gray-500">({stats.percentOnDeals}%)</span>
            )}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">On other marketplaces</dt>
          <dd className="text-gray-900">
            {stats.anyListedOnOther}
            {stats.totalListed > 0 && (
              <span className="ml-2 text-xs text-gray-500">({stats.percentOnOther}%)</span>
            )}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">Only on other marketplaces</dt>
          <dd className="text-gray-900">
            <span className={stats.listedOnOtherOnly > 0 ? 'text-amber-700 font-medium' : ''}>
              {stats.listedOnOtherOnly}
            </span>
            {stats.totalListed > 0 && (
              <span className="ml-2 text-xs text-gray-500">({stats.percentOnOtherOnly}%)</span>
            )}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">Listed on both</dt>
          <dd className="text-gray-900">{stats.listedOnBoth}</dd>
        </div>
        {stats.listedP2POnly > 0 && (
          <div className="flex justify-between">
            <dt className="text-gray-500">P2P only</dt>
            <dd className="text-gray-900">{stats.listedP2POnly}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function OtherMarketplaceListings({ listings }: { listings: AdminUserDetailResponse['otherMarketplaceListings'] }) {
  if (listings.length === 0) return null;

  const collectionHref = (l: AdminUserDetailResponse['otherMarketplaceListings'][number]) => {
    if (l.collectionSlug) return `/collections/${l.collectionSlug}`;
    if (l.collectionCreatorAddress && l.collectionTaxon !== null) {
      return `/admin/collections/${l.collectionCreatorAddress}/${l.collectionTaxon}`;
    }
    return null;
  };

  return (
    <div className="mt-6 border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b bg-amber-50">
        <h3 className="font-medium text-gray-900">
          Listed only on other marketplaces ({listings.length})
        </h3>
        <p className="text-xs text-gray-600 mt-0.5">
          NFTs this user has listed for sale only on marketplaces other than xrp.deals.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NFT</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collection</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lowest Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marketplaces</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Token ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {listings.map((l) => {
              const collHref = collectionHref(l);
              return (
                <tr key={l.nftTokenId}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <Link to={`/nfts/${l.nftTokenId}`} className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                      {l.imageUrl && (
                        <img src={l.imageUrl} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <span className="truncate max-w-xs">{l.name || 'Unnamed'}</span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {collHref ? (
                      <Link to={collHref} className="text-blue-600 hover:text-blue-800">
                        {l.collectionName || 'Unknown'}
                      </Link>
                    ) : (
                      <span className="text-gray-500">{l.collectionName || 'Unknown'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {l.lowestOtherPrice.toLocaleString(undefined, { maximumFractionDigits: 6 })} XRP
                    <div className="text-xs text-gray-500">{l.lowestOtherMarketplace}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div className="flex flex-wrap gap-1">
                      {l.marketplaces.map((m) => (
                        <span key={m} className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-800">{m}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs font-mono text-gray-500" title={l.nftTokenId}>
                    {l.nftTokenId.slice(0, 10)}…{l.nftTokenId.slice(-6)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OverviewTab({ data, formatDate }: { data: AdminUserDetailResponse; formatDate: (d: string | null | undefined) => string }) {
  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Contact Info */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Contact Information</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Email</dt>
              <dd className="text-gray-900">
                {data.user.email || 'Not set'}
                {data.user.emailConfirmed && <span className="ml-1 text-green-600">✓</span>}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Referral Code</dt>
              <dd className="font-mono text-gray-900">{data.user.referralCode || 'None'}</dd>
            </div>
            {data.user.referrer && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Referred By</dt>
                <dd>
                  <Link to={`/admin/users/${data.user.referrer.id}`} className="text-blue-600 hover:text-blue-800">
                    {data.user.referrer.username || data.user.referrer.address.slice(0, 12)}...
                  </Link>
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Social Links */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Social Links</h3>
          {data.user.socialLinks && Object.keys(data.user.socialLinks).length > 0 ? (
            <dl className="space-y-2 text-sm">
              {Object.entries(data.user.socialLinks).map(([platform, url]) => (
                <div key={platform} className="flex justify-between">
                  <dt className="text-gray-500 capitalize">{platform}</dt>
                  <dd>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                      Link
                    </a>
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-gray-500">No social links configured</p>
          )}
        </div>

        {/* Notification Preferences */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Notification Preferences</h3>
          <dl className="space-y-2 text-sm">
            {Object.entries(data.user.notificationPreferences).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <dt className="text-gray-500">{key.replace(/([A-Z])/g, ' $1').replace('notify On ', '').trim()}</dt>
                <dd className={value ? 'text-green-600' : 'text-gray-400'}>{value ? 'On' : 'Off'}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Wallets */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Connected Wallets ({data.wallets.length + 1})</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-mono text-gray-900">{data.user.xrplAddress.slice(0, 12)}...</span>
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">Primary</span>
            </div>
            {data.wallets.map((wallet) => (
              <div key={wallet.id} className="flex items-center justify-between">
                <span className="font-mono text-gray-900" title={wallet.address}>
                  {wallet.address.slice(0, 12)}...
                </span>
                <span className="text-gray-500">{wallet.nickname || 'No nickname'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Activity Stats */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Activity Statistics</h3>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <dt className="text-gray-500">NFTs Owned</dt>
              <dd className="text-lg font-semibold text-gray-900">{data.stats.nftCount}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Collection Offers</dt>
              <dd className="text-lg font-semibold text-gray-900">{data.stats.collectionOfferCount}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Transactions</dt>
              <dd className="text-lg font-semibold text-gray-900">{data.stats.transactionCount}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Alerts</dt>
              <dd className="text-lg font-semibold text-gray-900">{data.stats.alertCount}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Conversations</dt>
              <dd className="text-lg font-semibold text-gray-900">{data.stats.conversationCount}</dd>
            </div>
          </dl>
        </div>

        {/* Flags */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">Flags</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Show Migrate Banner</dt>
              <dd>
                <span className={`px-2 py-0.5 text-xs rounded ${data.user.showMigrateBanner ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                  {data.user.showMigrateBanner ? 'true' : 'false'}
                </span>
              </dd>
            </div>
          </dl>
        </div>

        {/* Listings */}
        <ListingStatsCard stats={data.listingStats} />

        {/* Feature Flags */}
        {data.user.features && Object.keys(data.user.features).length > 0 && (
          <div className="border rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Feature Flags</h3>
            <dl className="space-y-2 text-sm">
              {Object.entries(data.user.features).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <dt className="text-gray-500">{key}</dt>
                  <dd className="text-gray-900">{JSON.stringify(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>

      <OtherMarketplaceListings listings={data.otherMarketplaceListings} />
    </div>
  );
}

function NftsTab({ nfts }: { nfts: AdminUserDetailResponse['nfts'] }) {
  if (nfts.length === 0) {
    return <div className="p-6 text-center text-gray-500">No NFTs owned</div>;
  }

  return (
    <div className="p-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {nfts.map((nft) => (
          <div key={nft.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
            {nft.imageUrl ? (
              <img src={nft.imageUrl} alt={nft.name || ''} className="w-full aspect-square object-cover" />
            ) : (
              <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-gray-400">
                No Image
              </div>
            )}
            <div className="p-2">
              <p className="text-sm font-medium text-gray-900 truncate">{nft.name || 'Unnamed'}</p>
              <p className="text-xs text-gray-500 truncate">{nft.collectionName || 'Unknown Collection'}</p>
            </div>
          </div>
        ))}
      </div>
      {nfts.length >= 100 && (
        <p className="mt-4 text-sm text-gray-500 text-center">Showing first 100 NFTs</p>
      )}
    </div>
  );
}

function CollectionOffersTab({ offers, formatDate }: { offers: AdminUserDetailResponse['collectionOffers']; formatDate: (d: string | null | undefined) => string }) {
  if (offers.length === 0) {
    return <div className="p-6 text-center text-gray-500">No collection offers</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collection</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {offers.map((offer) => (
            <tr key={offer.id}>
              <td className="px-4 py-3 whitespace-nowrap text-sm">{offer.collectionName || 'Unknown'}</td>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{offer.amount.toFixed(2)} {offer.currency}</td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs rounded ${offer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {offer.status}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(offer.createdAt)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(offer.expiresAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransactionsTab({
  transactions,
  userAddresses,
  formatDate,
  truncateAddress,
}: {
  transactions: AdminUserDetailResponse['transactions'];
  userAddresses: string[];
  formatDate: (d: string | null | undefined) => string;
  truncateAddress: (addr: string) => string;
}) {
  if (transactions.length === 0) {
    return <div className="p-6 text-center text-gray-500">No transaction history</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NFT</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collection</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Counterparty</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {transactions.map((tx) => {
            const isSeller = tx.sellerAddress ? userAddresses.includes(tx.sellerAddress) : false;
            const counterparty = isSeller ? tx.buyerAddress : tx.sellerAddress;
            return (
              <tr key={tx.id}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">
                    {tx.type || 'Unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded ${isSeller ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    {isSeller ? 'Seller' : 'Buyer'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">{tx.nftName || 'Unknown'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{tx.collectionName || 'Unknown'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{tx.amount || 'N/A'} XRP</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-500">
                  {counterparty ? truncateAddress(counterparty) : 'N/A'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(tx.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {transactions.length >= 100 && (
        <p className="p-4 text-sm text-gray-500 text-center">Showing first 100 transactions</p>
      )}
    </div>
  );
}

function AlertsTab({ alerts, formatDate }: { alerts: AdminUserDetailResponse['alertConfigs']; formatDate: (d: string | null | undefined) => string }) {
  if (alerts.length === 0) {
    return <div className="p-6 text-center text-gray-500">No alerts configured</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Collection</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price Range</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Traits</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {alerts.map((alert) => (
            <tr key={alert.id}>
              <td className="px-4 py-3 whitespace-nowrap text-sm">{alert.collectionName || 'Unknown'}</td>
              <td className="px-4 py-3 whitespace-nowrap text-sm">
                <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                  {alert.notificationType.replace(/_/g, ' ')}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                {alert.minAmountInXrp || alert.maxAmountInXrp
                  ? `${alert.minAmountInXrp || '0'} - ${alert.maxAmountInXrp || '∞'} XRP`
                  : 'Any'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                {alert.traitFilters ? Object.keys(alert.traitFilters).length + ' filters' : 'None'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(alert.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotificationsTab({ notifications, formatDate }: { notifications: AdminUserDetailResponse['notifications']; formatDate: (d: string | null | undefined) => string }) {
  if (notifications.length === 0) {
    return <div className="p-6 text-center text-gray-500">No notifications</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Read</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emailed</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {notifications.map((notif) => (
            <tr key={notif.id} className={notif.readAt ? '' : 'bg-blue-50'}>
              <td className="px-4 py-3 whitespace-nowrap text-sm">
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                  {notif.type.replace(/_/g, ' ')}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                {JSON.stringify(notif.data).slice(0, 100)}...
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(notif.createdAt)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-sm">
                {notif.readAt ? <span className="text-green-600">✓</span> : <span className="text-gray-400">—</span>}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm">
                {notif.emailSentAt ? <span className="text-green-600">✓</span> : <span className="text-gray-400">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {notifications.length >= 50 && (
        <p className="p-4 text-sm text-gray-500 text-center">Showing last 50 notifications</p>
      )}
    </div>
  );
}

function FollowsTab({
  followedWallets,
  walletFollows,
  formatDate,
  truncateAddress,
}: {
  followedWallets: AdminUserDetailResponse['followedWallets'];
  walletFollows: AdminUserDetailResponse['walletFollows'];
  formatDate: (d: string | null | undefined) => string;
  truncateAddress: (addr: string) => string;
}) {
  return (
    <div className="p-6 space-y-8">
      {/* Followed Wallets */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Followed Wallets ({followedWallets.length})</h3>
        {followedWallets.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {followedWallets.map((fw) => (
              <div key={fw.id} className="border rounded p-2 text-sm font-mono">
                {truncateAddress(fw.walletAddress)}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No wallets followed</p>
        )}
      </div>

      {/* Wallet Follows for Notifications */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Wallet Notification Follows ({walletFollows.length})</h3>
        {walletFollows.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {walletFollows.map((wf) => (
              <div key={wf.id} className="border rounded p-2 text-sm font-mono">
                {truncateAddress(wf.followedAddress)}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No wallets followed for notifications</p>
        )}
      </div>
    </div>
  );
}

function ConversationsTab({
  conversations,
  collectionMessages,
  userAddresses,
  formatDate,
  truncateAddress,
}: {
  conversations: AdminUserDetailResponse['conversations'];
  collectionMessages: AdminUserDetailResponse['collectionMessages'];
  userAddresses: string[];
  formatDate: (d: string | null | undefined) => string;
  truncateAddress: (addr: string) => string;
}) {
  return (
    <div className="p-6 space-y-8">
      {/* Direct Conversations */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Direct Conversations ({conversations.length})</h3>
        {conversations.length > 0 ? (
          <div className="space-y-2">
            {conversations.map((conv) => {
              const otherParticipant = userAddresses.includes(conv.participant1) ? conv.participant2 : conv.participant1;
              return (
                <div key={conv.id} className="border rounded p-3 flex justify-between items-center">
                  <div>
                    <span className="font-mono text-sm">{truncateAddress(otherParticipant)}</span>
                    <span className="ml-2 text-sm text-gray-500">{conv.messageCount} messages</span>
                  </div>
                  <span className="text-sm text-gray-500">{formatDate(conv.lastMessageAt)}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No direct conversations</p>
        )}
      </div>

      {/* Collection Chat Messages */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Collection Chat Messages ({collectionMessages.length})</h3>
        {collectionMessages.length > 0 ? (
          <div className="space-y-2">
            {collectionMessages.map((msg) => (
              <div key={msg.id} className="border rounded p-3">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-sm font-medium">{msg.collectionName || 'Unknown Collection'}</span>
                  <span className="text-xs text-gray-500">{formatDate(msg.createdAt)}</span>
                </div>
                <p className="text-sm text-gray-600">{msg.content}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No collection chat messages</p>
        )}
        {collectionMessages.length >= 50 && (
          <p className="mt-2 text-sm text-gray-500">Showing last 50 messages</p>
        )}
      </div>
    </div>
  );
}

function ReferralsTab({
  referrals,
  referrer,
  formatDate,
  truncateAddress,
}: {
  referrals: AdminUserDetailResponse['referrals'];
  referrer: AdminUserDetailResponse['user']['referrer'];
  formatDate: (d: string | null | undefined) => string;
  truncateAddress: (addr: string) => string;
}) {
  return (
    <div className="p-6 space-y-8">
      {/* Referred By */}
      {referrer && (
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Referred By</h3>
          <div className="border rounded p-3">
            <Link to={`/admin/users/${referrer.id}`} className="text-blue-600 hover:text-blue-800">
              {referrer.username || truncateAddress(referrer.address)}
            </Link>
          </div>
        </div>
      )}

      {/* Users Referred */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3">Users Referred ({referrals.length})</h3>
        {referrals.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {referrals.map((ref) => (
                  <tr key={ref.id}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link to={`/admin/users/${ref.id}`} className="text-blue-600 hover:text-blue-800">
                        {ref.username || 'Anonymous'}
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-500">
                      {truncateAddress(ref.xrplAddress)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(ref.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No users referred</p>
        )}
      </div>
    </div>
  );
}

function SessionsTab({ sessions, formatDate }: { sessions: AdminUserDetailResponse['sessions']; formatDate: (d: string | null | undefined) => string }) {
  if (sessions.length === 0) {
    return <div className="p-6 text-center text-gray-500">No active sessions</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session ID</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sessions.map((session) => (
            <tr key={session.id}>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-500">
                {session.id.slice(0, 8)}...
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs rounded ${session.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {session.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(session.createdAt)}</td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(session.expiresAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditLogTab({ auditLog, formatDate }: { auditLog: AdminUserDetailResponse['auditLog']; formatDate: (d: string | null | undefined) => string }) {
  if (auditLog.length === 0) {
    return <div className="p-6 text-center text-gray-500">No audit log entries</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameters</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {auditLog.map((entry) => (
            <tr key={entry.id} className={entry.result ? '' : 'bg-red-50'}>
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{entry.action}</td>
              <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                {JSON.stringify(entry.parameters).slice(0, 80)}...
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs rounded ${entry.result ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {entry.result ? 'Success' : 'Failed'}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{entry.ipAddress || 'N/A'}</td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(entry.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {auditLog.length >= 50 && (
        <p className="p-4 text-sm text-gray-500 text-center">Showing last 50 audit log entries</p>
      )}
    </div>
  );
}
